import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import config from 'devextreme/core/config';
import { loadMessages, locale } from 'devextreme/localization';
import notify from 'devextreme/ui/notify';
import * as moment from 'moment-jalaali';
import { CookieService } from 'ngx-cookie-service';
import fa from '../../../../assets/i18n/fa-devextreme.json';
import { environment } from '../../../../environments/environment';
import { BytesPipe } from '../../../pipes/bytes.pipe';
import { JsonToStringPipe } from '../../../pipes/json-to-string.pipe';
import { BaseService } from '../../../providers/base.service';
import { AppTheme, applyTheme } from '../../../providers/theme';

@Component({
  template: ''
})
export class BaseComponent implements OnInit {
  constructor(
    public service: BaseService,
    public translator: TranslateService,
    public cookieService: CookieService,
    public titleService: Title) {
    if (!this.lang) { this.lang = 'fa'; }
    config({ rtlEnabled: this.rtl, defaultCurrency: 'IRR' });
    loadMessages(fa);
    locale(this.lang || 'fa');
    translator.setDefaultLang(this.lang || 'fa');
  }
  isNaN: Function = Number.isNaN;
  // staticData = new StaticDataService();
  loading = false;
  Math = Math;

  objectKeys = Object.keys;
  jsonToString = new JsonToStringPipe();
  bytesPipe = new BytesPipe();


  private _pageTitle: string;
  public get pageTitle(): string {
    return this._pageTitle;
  }
  public set pageTitle(v: string) {
    this._pageTitle = v;
    setTimeout(() => {
      this.titleService.setTitle(v);
    }, 100);
  }


  private _lang: string;
  public get lang(): string {
    if (!this._lang) {
      this._lang = this.cookieService.get('lang') || 'fa';
    }
    return this._lang;
  }
  public set lang(v: string) {
    if (this._lang != v) {
      this._lang = v;
      this.cookieService.set('lang', this._lang, undefined, undefined, undefined, false, 'Strict');
      this.translator.setDefaultLang(this.lang);
      document.dir = this.lang == 'fa' ? 'rtl' : 'ltr';
      document.location.reload();
    }
  }


  private _theme: AppTheme;
  public get theme(): AppTheme {
    if (!this._theme) {
      this._theme = this.cookieService.get('theme') === 'dark' ? 'dark' : 'light';
    }
    return this._theme;
  }
  public set theme(v: AppTheme) {
    if (this._theme != v) {
      this._theme = v;
      this.cookieService.set('theme', this._theme, undefined, undefined, undefined, false, 'Strict');
      // Unlike `lang`, this needs no reload: the DevExtreme stylesheet is swapped
      // in place and the app's own palette follows the .dark-theme marker class.
      applyTheme(this._theme);
    }
  }

  toggleTheme() {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
  }


  private _token: string;
  public get token(): string {
    this._token = this.cookieService.get('token');
    return this._token;
  }
  public set token(v: string) {
    if (v !== this._token) {
      this._token = v;
      this.cookieService.set('token', this._token, undefined, undefined, undefined, false, 'Strict');
    }
  }

  private _username: string;
  public get username(): string {
    this._username = this.cookieService.get('username');
    return this._username;
  }
  public set username(v: string) {
    if (v !== this._username) {
      this._username = v;
      this.cookieService.set('username', this._username, undefined, undefined, undefined, false, 'Strict');
    }
  }

  private _role: string;
  public get role(): string {
    this._role = this.cookieService.get('role');
    return this._role;
  }
  public set role(v: string) {
    if (v !== this._role) {
      this._role = v;
      this.cookieService.set('role', this._role, undefined, undefined, undefined, false, 'Strict');
    }
  }

  private _ownRole: string;
  public get ownRole(): string {
    this._ownRole = this.cookieService.get('own_role');
    return this._ownRole;
  }
  public set ownRole(v: string) {
    if (v !== this._ownRole) {
      this._ownRole = v;
      this.cookieService.set('own_role', this._ownRole, undefined, undefined, undefined, false, 'Strict');
    }
  }

  private _internalLine: string;
  public get internalLine(): string {
    this._internalLine = this.cookieService.get('internal_line');
    return this._internalLine;
  }
  public set internalLine(v: string) {
    if (v !== this._internalLine) {
      this._internalLine = v;
      this.cookieService.set('internal_line', this._internalLine, undefined, undefined, undefined, false, 'Strict');
    }
  }

  private _selectedAccount: string;
  public get selectedAccount(): string {
    this._selectedAccount = sessionStorage.getItem('selectedAccount');
    return this._selectedAccount;
  }
  public set selectedAccount(v: string) {
    if (v !== this._selectedAccount) {
      this._selectedAccount = v;
      sessionStorage.setItem('selectedAccount', this._selectedAccount);
    }
  }

  private _selectedUser: string;
  public get selectedUser(): string {
    this._selectedUser = sessionStorage.getItem('selectedUser');
    return this._selectedUser;
  }
  public set selectedUser(v: string) {
    if (v !== this._selectedUser) {
      this._selectedUser = v;
      sessionStorage.setItem('selectedUser', this._selectedUser);
    }
  }

  private _legacyId: string;
  public get legacyId(): string {
    this._legacyId = this.cookieService.get('legacy_id');
    return this._legacyId;
  }
  public set legacyId(v: string) {
    if (v !== this._legacyId) {
      this._legacyId = v;
      this.cookieService.set('legacy_id', this._legacyId, undefined, undefined, undefined, false, 'Strict');
    }
  }


  private _latitude: number;
  public get latitude(): number {
    if (!this._latitude) {
      this._latitude = +this.cookieService.get('latitude');
    }
    return this._latitude;
  }
  public set latitude(v: number) {
    this._latitude = v;
    this.cookieService.set('latitude', this._latitude.toString(), undefined, undefined, undefined, false, 'Strict');
  }


  private _longitude: number;
  public get longitude(): number {
    if (!this._longitude) {
      this._longitude = +this.cookieService.get('longitude');
    }
    return this._longitude;
  }
  public set longitude(v: number) {
    this._longitude = v;
    this.cookieService.set('longitude', this._longitude.toString(), undefined, undefined, undefined, false, 'Strict');
  }


  public get rtl(): boolean {
    return this.lang === 'fa';
  }

  public get font(): string {
    return this.lang === 'fa' ? 'samim' : 'Tahoma';
  }

  public get roles() {
    return this.role.replace(/ /g, '').split(',');
  }

  public get isMobile(): boolean {
    // return window && window.screen.width < 576;
    return /Mobi|Android/i.test(navigator.userAgent);
  }

  uploadUrl: string = `${environment.url}api/upload/addFile`;
  production: boolean = environment.production;

  clearCookie() {
    // Read before the wipe: language and theme are UI preferences, not session
    // state, and should survive a logout.
    const lang = this.lang;
    const theme = this.theme;
    this.cookieService.deleteAll();
    this.cookieService.set('lang', lang, undefined, undefined, undefined, false, 'Strict');
    this.cookieService.set('theme', theme, undefined, undefined, undefined, false, 'Strict');
    // sessionStorage holds the cached menu tree that AccessGuard authorises
    // against, plus selectedAccount/selectedUser/workLogId. Dropping only the
    // cookies leaves all of that behind for whoever logs in next in this tab,
    // so the next user is authorised against the previous user's menus until
    // they happen to be refetched. logOut() always did this; the expired-token
    // paths on the login screen did not.
    sessionStorage.clear();
  }

  async ngOnInit() {
    this.pageTitle = 'پتیاک من';
    if (this.role.includes('PERSONNEL')) {
      try {
        this.cookLocation();
      } catch (error) {
        console.error(error)
      }
      this.service.socket.emit('auth', this.token);
    }
  }

  protected cookLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
      }, (error) => {
      });
    }
  }

  formatMoney(n: any) {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' ﷼';
  }

  formatNumber(n: any) {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  // formatBytes(n: any) {
  //   return new BytesPipe().transform(n);
  // }

  formatBytes(bytes: number): string {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (!bytes) {
      return '0';
    }
    var sign = bytes > 0 ? ' ' : '- ';
    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024));
    return parseFloat((Math.abs(bytes) / Math.pow(1024, i)).toFixed(2)) + sign + sizes[i];
  }

  formatObject(o: any) {
    return new JsonToStringPipe().transform(o);
  }

  formatSeconds(n: any) {
    const hours = Math.floor(n / 3600);
    n %= 3600;
    const minutes = Math.floor(n / 60);
    const seconds = n % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  formatMinutes(n: any) {
    const hours = Math.floor(n / 60);
    n %= 60;
    const minutes = n % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  }

  formatTranslate(s: any) {
    return this.translator.instant(s.toUpperCase());
  }

  hasRole(role) {
    return this.role && this.role.includes(role);
  }

  dateFormat(message) {
    const from = message ? message.year + '/' + message.month + '/' + message.day : null;
    return message ? moment(from, 'jYYYY/jMM/jDD').locale('en').format('YYYY/MM/DD HH:mm:ss') : null;
  }

  unique(array, prop) {
    return array.reduce((a, d) => {
      if (!a.includes(d[prop])) { a.push(d[prop]); }
      return a;
    }, []);

  }

  toggleLang() {
    if (this.lang === 'en') {
      this.lang = 'fa';
    } else {
      this.lang = 'en';
    }

  }

  typeOf(value) {
    return typeof value;
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  handleError(error) {
    try {
      if (error.error) {
        error = error.error;
      }
      if (!environment.production) {
        console.error(error);
      }

      let message = this.translator.instant(error?.message?.message || error?.message || error || 'GENERAL_ERROR');
      // if (error.message) {
      //   message = this.translator.instant(error.message);
      // } else if (typeof error === 'string') {
      //   message = this.translator.instant(error);
      // } else {
      //   try {
      //     message = this.translator.instant(error?.message) || message;
      //   } catch (err) {
      //     console.error(err);
      //   }
      // }

      notify(message, 'error', 7000);

    } catch (e) {
      notify(this.translator.instant('GENERAL_ERROR'), 'error', 7000);
    }
  }

  handleSuccess(message?, displayTime?) {
    notify(message || this.translator.instant('DONE_MSG'), 'success', displayTime || 3000);
  }

  handleWarning(message, displayTime?) {
    notify(message, 'warning', displayTime || 5000);
  }

  handleInfo(message, displayTime?) {
    notify(message, 'info', displayTime || 8000);
  }

}
